/**
 * Manager-Export: reine Stundenliste fuer das transitive Team eines Managers.
 *
 * Bewusst getrennt vom HR-Lohn-Pfad (payrollExport.ts): Manager duerfen
 * keine Lohndaten (hourlyWage, monthlySalary) sehen. Diese Datei kennt diese
 * Felder nicht — Input ist ausschliesslich `DayEntry`-Maps.
 */

import { addDays, format } from 'date-fns';
import { de } from 'date-fns/locale';
import type { DayEntry } from '../types';
import { formatDE } from './payrollPeriod';

const BOM = '\uFEFF';
const CSV_HEADER = 'Datum;Wochentag;Arbeitsstunden;Feiertag;Urlaub;Krank;Bemerkung';

/**
 * Parst einen ISO-Datums-String (YYYY-MM-DD) in ein lokales Date.
 * Defensive: bei ungueltigem Input auf 1970-01-01 zurueckfallen (map(Number)
 * liefert NaN bei Nicht-Zahlen \u2014 der ??-Operator wuerde nicht greifen).
 */
export function parseISODate(iso: string): Date {
  const parts = iso.split('-').map(Number);
  const y = Number.isFinite(parts[0]) ? (parts[0] as number) : 1970;
  const m = Number.isFinite(parts[1]) ? (parts[1] as number) : 1;
  const d = Number.isFinite(parts[2]) ? (parts[2] as number) : 1;
  return new Date(y, m - 1, d);
}

/**
 * CSV-Feld escapen (Semikolon, Anfuehrungszeichen, Newline → in "..." setzen)
 * + CSV-Injection-Schutz: Werte, die mit =, +, -, @ beginnen, mit ' praefixen.
 * OWASP Empfehlung gegen Formula-Injection in Excel/LibreOffice.
 */
function csvField(value: string): string {
  let v = value;
  if (v.length > 0 && /^[=+\-@]/.test(v)) {
    v = `'${v}`;
  }
  if (v.includes(';') || v.includes('"') || v.includes('\n')) {
    return `"${v.replace(/"/g, '""')}"`;
  }
  return v;
}

function downloadBlob(content: BlobPart, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const link = document.createElement('a');
  const href = URL.createObjectURL(blob);
  link.href = href;
  link.download = filename;
  link.click();
  // revoke verzoegert: auf langsamen Systemen koennte ein sofortiges revoke
  // den Download abbrechen, bevor der Browser den Blob angefordert hat.
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

/**
 * Spalten + Summen fuer den Tagesdetails-Export — pure Berechnung, ohne IO.
 * Gibt eine Liste von Zeilen-Tupeln zurueck (Header gehoert nicht dazu).
 */
export interface DailyRow {
  date: string;        // dd.MM.yyyy
  weekday: string;
  hours: number;
  isHoliday: boolean;
  vacationCell: 'Ja' | '½' | 'Nein'; // ½ fuer halben Urlaub
  isSick: boolean;
  note: string;
}

export interface DailyRangeData {
  rows: DailyRow[];
  totals: {
    hours: number;
    holidays: number;
    vacation: number;
    sick: number;
  };
}

export function buildDailyRange(
  entries: Record<string, DayEntry>,
  from: Date,
  to: Date,
): DailyRangeData {
  if (from > to) {
    return { rows: [], totals: { hours: 0, holidays: 0, vacation: 0, sick: 0 } };
  }

  const rows: DailyRow[] = [];
  let totalHours = 0;
  let totalHolidays = 0;
  let totalVacation = 0;
  let totalSick = 0;

  let current = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());

  while (current <= end) {
    const dateStr = format(current, 'yyyy-MM-dd');
    const entry = entries[dateStr];
    const hours = entry?.hours ?? 0;
    const isHoliday = entry?.isHoliday ?? false;
    const isVacation = entry?.isVacation ?? false;
    const isHalfVacation = entry?.isHalfVacation ?? false;
    const isSick = entry?.isSickDay ?? false;
    const note = entry?.note ?? '';

    rows.push({
      date: format(current, 'dd.MM.yyyy'),
      weekday: format(current, 'EEEE', { locale: de }),
      hours,
      isHoliday,
      vacationCell: isVacation ? 'Ja' : (isHalfVacation ? '½' : 'Nein'),
      isSick,
      note,
    });

    totalHours += hours;
    if (isHoliday) totalHolidays += 1;
    if (isVacation) totalVacation += 1;
    if (isHalfVacation) totalVacation += 0.5;
    if (isSick) totalSick += 1;

    current = addDays(current, 1);
  }

  return {
    rows,
    totals: {
      hours: totalHours,
      holidays: totalHolidays,
      vacation: totalVacation,
      sick: totalSick,
    },
  };
}

function rangeFilenamePart(from: Date, to: Date): string {
  return `${format(from, 'yyyy-MM-dd')}_${format(to, 'yyyy-MM-dd')}`;
}

/** Datei-Slug aus User-Name: nur a-z0-9_-, max. 60 Zeichen. */
function safeFilenamePart(name: string): string {
  const slug = name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
  return slug.length > 0 ? slug : 'mitarbeiter';
}

/**
 * Excel-Sheet-Name sanitisieren.
 * - verbotene Zeichen: [ ] : * ? / \  → durch _ ersetzen
 * - Apostroph an Anfang/Ende verboten → entfernen
 * - max. 31 Zeichen
 * - leerer Name → 'Sheet'
 */
export function sanitizeSheetName(name: string): string {
  let n = name.replace(/[[\]:*?/\\]/g, '_');
  n = n.replace(/^'+|'+$/g, '');
  n = n.trim();
  if (n.length === 0) n = 'Sheet';
  if (n.length > 31) n = n.slice(0, 31);
  return n;
}

/**
 * Dedupliziert Sheet-Namen durch Anhaengen von " (2)", " (3)", ... .
 * Respektiert das 31-Zeichen-Limit von Excel auch nach Suffix.
 */
export function dedupeSheetNames(names: string[]): string[] {
  const seen = new Map<string, number>();
  const usedNames = new Set<string>();
  const result: string[] = [];
  for (const raw of names) {
    const base = sanitizeSheetName(raw);
    const first = (seen.get(base) ?? 0) === 0;
    if (first && !usedNames.has(base)) {
      seen.set(base, 1);
      usedNames.add(base);
      result.push(base);
      continue;
    }
    // Naechste freie Suffix-Nummer suchen (O(n) im worst case, fuer realistische
    // Team-Groessen vernachlaessigbar). Verwendet usedNames-Set fuer O(1)-Checks.
    let count = (seen.get(base) ?? 0) + 1;
    let candidate = '';
    for (; count < 100000; count += 1) {
      const suffix = ` (${count})`;
      const maxBase = 31 - suffix.length;
      const truncated = base.length > maxBase ? base.slice(0, maxBase) : base;
      candidate = `${truncated}${suffix}`;
      if (!usedNames.has(candidate)) break;
    }
    seen.set(base, count);
    usedNames.add(candidate);
    result.push(candidate);
  }
  return result;
}

/**
 * CSV-Export einer Stundenliste fuer einen Mitarbeiter in einem beliebigen Datumsbereich.
 */
export function exportDailyRangeCSV(
  entries: Record<string, DayEntry>,
  from: Date,
  to: Date,
  userName: string,
): void {
  const data = buildDailyRange(entries, from, to);

  const lines: string[] = [];
  lines.push(`${csvField(userName)};;;;;;`);
  lines.push(`${csvField(`Zeitraum: ${format(from, 'dd.MM.yyyy')} – ${format(to, 'dd.MM.yyyy')}`)};;;;;;`);
  lines.push('');
  lines.push(CSV_HEADER);

  for (const row of data.rows) {
    lines.push([
      row.date,
      row.weekday,
      formatDE(row.hours),
      row.isHoliday ? 'Ja' : 'Nein',
      row.vacationCell,
      row.isSick ? 'Ja' : 'Nein',
      csvField(row.note),
    ].join(';'));
  }

  // Zwischensumme
  lines.push([
    '"Summe"',
    '',
    formatDE(data.totals.hours),
    data.totals.holidays.toString(),
    formatDE(data.totals.vacation),
    data.totals.sick.toString(),
    '',
  ].join(';'));

  const content = BOM + lines.join('\r\n');
  const filename = `stundenliste_${safeFilenamePart(userName)}_${rangeFilenamePart(from, to)}.csv`;
  downloadBlob(content, filename, 'text/csv;charset=utf-8;');
}

export interface TeamExportMember {
  userId: string;
  userName: string;
  entries: Record<string, DayEntry>;
}

/**
 * XLSX-Export mit einem Arbeitsblatt pro Team-Mitglied.
 * Lädt exceljs dynamisch (Code-Splitting), damit das Hauptbundle klein bleibt.
 */
export async function exportTeamDailyXLSX(
  team: TeamExportMember[],
  from: Date,
  to: Date,
): Promise<void> {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Stundenliste';
  workbook.created = new Date();

  const sorted = [...team].sort((a, b) => a.userName.localeCompare(b.userName, 'de'));
  const sheetNames = dedupeSheetNames(sorted.map((m) => m.userName));

  sorted.forEach((member, idx) => {
    const sheet = workbook.addWorksheet(sheetNames[idx]!);

    sheet.columns = [
      { header: 'Datum', key: 'date', width: 12 },
      { header: 'Wochentag', key: 'weekday', width: 12 },
      { header: 'Arbeitsstunden', key: 'hours', width: 16 },
      { header: 'Feiertag', key: 'isHoliday', width: 10 },
      { header: 'Urlaub', key: 'vacationCell', width: 10 },
      { header: 'Krank', key: 'isSick', width: 10 },
      { header: 'Bemerkung', key: 'note', width: 40 },
    ];

    // Header-Zeile fett
    sheet.getRow(1).font = { bold: true };

    const data = buildDailyRange(member.entries, from, to);

    for (const row of data.rows) {
      const note = sanitizeCellValue(row.note);
      sheet.addRow({
        date: row.date,
        weekday: row.weekday,
        hours: row.hours,
        isHoliday: row.isHoliday ? 'Ja' : 'Nein',
        vacationCell: row.vacationCell,
        isSick: row.isSick ? 'Ja' : 'Nein',
        note,
      });
    }

    // Summen-Zeile
    const totalsRow = sheet.addRow({
      date: 'Summe',
      weekday: '',
      hours: data.totals.hours,
      isHoliday: data.totals.holidays,
      vacationCell: data.totals.vacation,
      isSick: data.totals.sick,
      note: '',
    });
    totalsRow.font = { bold: true };

    // Stunden-Spalte als Zahl mit 2 Dezimalstellen formatieren
    sheet.getColumn('hours').numFmt = '#,##0.00';
  });

  // Falls Team leer ist, ein leeres Hinweis-Sheet einfuegen — sonst wirft exceljs.
  if (sorted.length === 0) {
    const sheet = workbook.addWorksheet('Leer');
    sheet.addRow(['Keine Team-Mitglieder im gewählten Zeitraum.']);
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const filename = `stundenliste_team_${rangeFilenamePart(from, to)}.xlsx`;
  downloadBlob(
    buffer,
    filename,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  );
}

/**
 * Zellwert fuer XLSX gegen Formula-Injection absichern. ExcelJS schreibt Strings
 * mit fuehrendem =/+/-/@ als Formel — wir praefixen mit ', damit Excel den Wert
 * als Text behandelt.
 */
function sanitizeCellValue(value: string): string {
  if (value.length > 0 && /^[=+\-@]/.test(value)) {
    return `'${value}`;
  }
  return value;
}
